import awsconfig from '../aws-exports';
import { oneSentence } from './misc';
import API from '@aws-amplify/api';
import axios from 'axios';
import debug from 'debug';
import { CDN_ENABLED, CDN_ONLY, LOG_TIMING } from 'environment';
// import find from 'lodash/find';
import get from 'lodash/get';
// import set from 'lodash/set';
import striptags from 'striptags';
import { CGPage, CGPageData } from 'types/shopify';

const d = debug('amplify');

const SHOPIFYPROXYNAME = 'shopifyproxy';

export const getUGCCardImageByRef = (ref: string) => {
  return awsconfig.aws_content_delivery_url + '/ugc/' + ref;
};

export const getShopifyPageByHandle = async (handle: string, refresh: boolean = false) => {
  if (LOG_TIMING) console.time('loadPage-' + handle);
  if (!refresh && CDN_ENABLED()) {
    return axios
      .get(awsconfig['aws_content_delivery_url'] + '/cache/page/' + handle + '.json')
      .then((response) => {
        const Page = get(response, 'data.data', false);
        if (Page) {
          if (LOG_TIMING) console.timeEnd('loadPage-' + handle);
          return processAndReturnPage(Page);
        } else {
          d('cachedPageNotFound', handle, response);
          // If we're in CDN_ONLY mode, just return a 404 STAT....
          if (CDN_ONLY) throw new Error('404');
          return getApiPage(handle);
        }
      })
      .catch((err) => {
        d('caughtPageError', err);
        if (err?.message.includes('404')) throw new Error('404');
        return getApiPage(handle);
      });
  } else {
    return getApiPage(handle);
  }
};

const getApiPage = async (handle: string) => {
  const cacheCrusher = '?_' + new Date().getTime();
  return API.get(SHOPIFYPROXYNAME, '/page/' + handle + cacheCrusher, {})
    .then((res) => {
      if (LOG_TIMING) console.timeEnd('loadPage-' + handle);
      d('getShopifyPageByHandle', res);
      const page: CGPage = res.data;
      if (page) return processAndReturnPage(page);
      throw new Error('PAGE_LOAD_ERROR');
    })
    .catch((err) => {
      d('caughtApiPageError', err);
      throw new Error('404');
    });
};

const processAndReturnPage = (page: any) => {
  // Flatten metafields...
  page.metafields.forEach((Metafield: any) => {
    if (Metafield.value_type === 'json_string') {
      const parsed = JSON.parse(Metafield.value);
      page[Metafield.key] = {
        ...parsed,
        value: Metafield.value,
      };
    } else {
      page[Metafield.key] = {
        value: Metafield.value,
      };
    }
  });

  // Extract PD
  let pd = extractPageData(page.body_html || '');

  return { ...page, pd };
};

export const getShopifyProductByHandle = async (handle: string) => {
  if (LOG_TIMING) console.time('loadProduct-' + handle);
  if (CDN_ENABLED()) {
    return axios
      .get(awsconfig['aws_content_delivery_url'] + '/cache/product/' + handle + '.json')
      .then((response) => {
        const Product = get(response, 'data.data', false);
        if (Product) {
          if (LOG_TIMING) console.timeEnd('loadProduct-' + handle);
          return processAndReturnProduct(Product);
        } else {
          d('cachedProductNotFound', handle, response);
          // If we're in CDN_ONLY mode, just return a 404 STAT....
          if (CDN_ONLY) throw new Error('404');
          return getApiProduct(handle);
        }
      })
      .catch((err) => {
        d('caughtProductError', err);
        if (err?.message.includes('404')) throw new Error('404');
        return getApiProduct(handle);
      });
  } else {
    return getApiProduct(handle);
  }
};

const getApiProduct = async (handle: string) => {
  d('getApiProduct', handle);
  const cacheCrusher = '?_' + new Date().getTime();
  return API.get('shopifyproxy', '/product/' + handle + cacheCrusher, {})
    .then((res) => {
      d('getApiProductResponse', res);
      if (LOG_TIMING) console.timeEnd('loadProduct-' + handle);
      if (!res) throw new Error('ERROR_LOADING_PRODUCT');
      const Product = res.data;
      if (Product) {
        return processAndReturnProduct(Product);
      } else {
        throw new Error('ERROR_FINDING_PRODUCT');
      }
    })
    .catch((err) => {
      d('caughtApiProductError', err);
      throw new Error('404');
    });
};

export const getShopifyCollectionByHandle = async (handle: string) => {
  if (LOG_TIMING) console.time('loadCollection-' + handle);
  if (CDN_ENABLED()) {
    return axios
      .get(awsconfig['aws_content_delivery_url'] + '/cache/collection/' + handle + '.json')
      .then((response) => {
        const Collection = get(response, 'data.data', false);
        if (Collection) {
          if (LOG_TIMING) console.timeEnd('loadCollection-' + handle);
          return processAndReturnCollection(Collection);
        } else {
          d('cachedCollectionNotFound', handle);
          // If we're in CDN_ONLY mode, just return a 404 STAT....
          if (CDN_ONLY) throw new Error('404');
          return getApiCollection(handle);
        }
      })
      .catch((err) => {
        d('caughtCollectionError', err);
        if (err?.message.includes('404')) throw new Error('404');
        return getApiCollection(handle);
        // throw new Error(err);
      });
  } else {
    return getApiCollection(handle);
  }
};

const getApiCollection = async (handle: string) => {
  d('getApiCollection', handle);
  const cacheCrusher = '?_' + new Date().getTime();
  return API.get('shopifyproxy', '/collection/' + handle + cacheCrusher, {})
    .then((res) => {
      d('getApiCollectionResponse', res);
      if (LOG_TIMING) console.timeEnd('loadCollection-' + handle);
      if (!res) throw new Error('ERROR_LOADING_COLLECTION');
      const Collection = res.data;
      if (Collection) {
        return processAndReturnCollection(Collection);
      } else {
        throw new Error('ERROR_LOADING_COLLECTION');
      }
    })
    .catch((err) => {
      d('caughtApiCollectionError', err);
      throw new Error('404');
    });
};

export const processAndReturnProduct = (product: any) => {
  // Flatten the images...
  if (product.images) {
    product.images = product.images.edges.map((imageEdge: any) => {
      return {
        src: imageEdge.node.src,
      };
    });
  }
  // Flatten the variants...
  if (product.variants) {
    product.variants = flattenProductVariantEdges(product.variants.edges);
  }
  // Extract featureImage...
  if (product.images && product.images[0]) product.featuredImage = product.images[0];
  // Extract PD
  let pd = extractPageData(product.description || '');
  // // TODO - Extract the abstract metafield...
  if (!pd.abstract) {
    pd.abstract = oneSentence(product.description || '').slice(0, 50);
  }

  return { ...product, pd };
};

export const processAndReturnCollection = (Collection: any) => {
  if (Collection.listCollections) {
    // TODO - add better validation in here...
    Collection.listCollections.parsed = JSON.parse(Collection.listCollections.value);
  }
  if (Collection.highlightProducts) {
    // TODO - add better validation in here...
    const parsed = JSON.parse(Collection.highlightProducts.value);
    Collection.highlightProducts = {
      ...Collection.highlightProducts,
      handles: parsed.handles,
    };
  }

  // Flatten metafields...
  const metafields = Collection.metafields ? flattenMetafieldEdges(Collection.metafields.edges || []) : {};

  // Flatten products
  Collection.products = flattenProductEdges(Collection.products.edges);

  // Extract PD
  let pd = extractPageData(Collection.description || '');
  // // TODO - Extract the abstract metafield...
  if (!pd.abstract) {
    pd.abstract = oneSentence(Collection.description || '').slice(0, 50);
  }

  return { ...Collection, pd, ...metafields };
};

export const getShopifyCollections = async () => {
  if (LOG_TIMING) console.time('loadCollections');
  if (CDN_ENABLED()) {
    return axios
      .get(awsconfig['aws_content_delivery_url'] + '/cache/collections.json')
      .then((response) => {
        const Collections = get(response, 'data.data', false);
        if (Collections) {
          if (LOG_TIMING) console.timeEnd('loadCollections');
          return Collections.map((Collection: any) => processAndReturnCollection(Collection));
        } else {
          d('cachedCopyNotFound');
          return getApiCollections();
        }
      })
      .catch((err) => {
        d('caught', err);
        return getApiCollections();
        // throw new Error(err);
      });
  } else {
    return getApiCollections();
  }
};

const getApiCollections = async () => {
  d('getApiCollections');
  const cacheCrusher = '?_' + new Date().getTime();
  return API.get('shopifyproxy', '/collections' + cacheCrusher, {}).then((res) => {
    d('getApiCollectionsResponse', res);
    if (LOG_TIMING) console.timeEnd('loadCollections');
    if (!res) throw new Error('ERROR_LOADING_COLLECTIONS');
    const Collections = res.data;
    if (Collections) {
      return Collections.map((Collection: any) => processAndReturnCollection(Collection));
    } else {
      throw new Error('ERROR_LOADING_COLLECTION');
    }
  });
};

const flattenProductEdges = (edges: any[]) => {
  return edges.map((productEdge: any) => {
    const Product = productEdge.node;
    if (Product.images) {
      Product.images = Product.images.edges.map((imageEdge: any) => {
        return {
          src: imageEdge.node.originalSrc,
        };
      });
    }

    // Create a featuredImage
    if (!Product.featuredImage && Product.images && Product.images.length) {
      Product.featuredImage = {
        src: Product.images[0].src,
      };
    }

    let pd = extractPageData(Product.description || '');
    // // TODO - Extract the abstract metafield...
    if (!pd.abstract) {
      pd.abstract = oneSentence(Product.description || '').slice(0, 50);
    }
    return {
      ...Product,
      pd,
    };
  });
};

const flattenProductVariantEdges = (edges: any[]) => {
  return edges.map((variantEdge: any) => {
    const Variant = variantEdge.node;
    return {
      ...Variant,
    };
  });
};

const flattenMetafieldEdges = (edges: any[]) => {
  const returnValue: any = {};
  edges.forEach((metafieldEdge: any) => {
    const Metafield = metafieldEdge.node;
    if (Metafield.valueType === 'JSON_STRING') {
      const parsed = JSON.parse(Metafield.value);
      returnValue[Metafield.key] = {
        ...parsed,
        value: Metafield.value,
      };
    } else {
      returnValue[Metafield.key] = {
        value: Metafield.value,
      };
    }
  });
  return returnValue;
};

const extractTagAttributes = (html: string) => {
  html = striptags(html); // Remove any tags from custom attributes
  html = html.replace(/\[pd([^\]]+)\]/g, '<pd$1>');
  const parser = new DOMParser();
  const docNode = parser.parseFromString(html, 'text/xml');
  // @ts-ignore
  const key = docNode.firstElementChild.getAttribute('key');
  // @ts-ignore
  const value = docNode.firstElementChild.getAttribute('value');
  return {
    key,
    value,
  };
};

// const extractGenericAttributes = (html: string) => {
//   html = striptags(html); // Remove any tags from custom attributes
//   html = html.replace(/\[([^\]]+)\]/g, '<$1>');
//   const parser = new DOMParser();
//   const docNode = parser.parseFromString(html, 'text/xml');
//   const foundAttributes = {};

//   const attrCount = docNode.firstElementChild?.attributes.length || 0;
//   for (let x = 0; x < attrCount; x++) {
//     const attr = docNode.firstElementChild?.attributes.item(x);
//     if (attr && attr?.name) {
//       set(foundAttributes, attr?.name, attr?.value);
//     }
//   }
//   return foundAttributes;
// };

export const extractPageData = (content: string): CGPageData => {
  const dataAttributes: any = {};

  // Added support to extract the product brands... only one brand supported
  // const brands = content.match(/\[brands([^\]]+)\]/g);
  // brands?.forEach((brand) => {
  //   const extracted = extractGenericAttributes(brand);
  //   dataAttributes['brands'] = extracted;
  // });

  const snippets = content.match(/\[pd([^\]]+)\]/g);
  snippets?.forEach((snippet) => {
    const extracted = extractTagAttributes(snippet);
    if (extracted.key && extracted.value) {
      switch (extracted.key) {
        case 'addCardBorder':
          dataAttributes[extracted.key] = !!(extracted.value === 'true');
          break;
        case 'tags':
          dataAttributes[extracted.key] = extracted.value.split('|');
          break;
        default:
          dataAttributes[extracted.key] = extracted.value;
          break;
      }
    }
  });
  return dataAttributes;
};
